import React, { useState, useMemo } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ScrambleText } from '../components/ui/ScrambleText';
import { SynapsesAnimation } from '../components/ui/SynapsesAnimation';

export default function JoinUs({ id }) {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { margin: '-40% 0px -40% 0px' });
  const [resultMessage] = useState('');
  
  // Check if device is mobile
  const isMobile = useMemo(() => {
    if (typeof window !== 'undefined') {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    }
    return false;
  }, []);

  if (isMobile && ref.current) {
    ref.current.style.touchAction = 'manipulation'; // ✅ allow scroll and tap
    ref.current.style.pointerEvents = 'auto';       // ✅ allow normal interaction (but don't block scroll)
  }

  return (
    <motion.section
      id={id}
      ref={ref}
      className="px-6 flex items-center justify-center w-full min-h-[100svh] py-20 snap-start relative overflow-y-auto"
      initial={{ opacity: 0, y: 0 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      <div className="container max-w-10xl mx-auto flex flex-col md:flex-row items-center justify-center px-5 w-full">
        <div className="flex md:mr-10 items-center justify-center mb-8 md:mb-0">
          <motion.div
            className="w-[400px] h-[400px] md:h-[500px] md:w-[500px]"
            initial={{ opacity: 0, y: 60 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.25, ease: "easeOut", delay: 0.1 }}
          >
            <SynapsesAnimation enableControls={!isMobile} />
          </motion.div>
        </div>

        <div className="flex flex-col md:pr-6 w-full md:w-auto text-center md:text-left md:items-start items-center">
          <motion.h2
            className="text-3xl md:text-4xl font-extrabold text-white leading-tight text-center md:text-left"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.4, ease: "easeOut", delay: 0.2 }}
          >
            <ScrambleText text="This feels like 2 AM in the lab" enabled={isInView} />
          </motion.h2>

          <motion.p
            className="text-base text-[#4F6A63] font-mono leading-loose text-center md:text-left mt-4"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.3 }}
          >
            <ScrambleText text="We care about making incredible memories as much as groundbreaking discoveries. If you're yearning for that time when it just felt right, we know the feeling." enabled={isInView} />
          </motion.p>

          <motion.div
            className="mt-6 flex flex-col items-center md:items-start gap-2"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.4 }}
          >
            <Link
              to="/about-us"
              className="text-sm md:text-base font-mono text-[#00CF9D] hover:underline"
            >
              Want the full picture? Explore our vision.
            </Link>
          </motion.div>

          {resultMessage && (
            <div className="mt-4 text-center md:text-left text-white w-full">
              {resultMessage}
            </div>
          )}
        </div>
      </div>
    </motion.section>
  );
}
